import React, { FC, FocusEvent } from 'react';
import {
	Box,
	Typography,
	Checkbox,
	FormControlLabel,
	MenuItem,
	RadioGroup,
	Radio,
	FormGroup,
} from '@mui/material';
import Link from 'next/link';
import { CheckRounded } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { IntakeFormSectionField, EventIntakeFormSettings, IntakeFormValue } from '@sweatpals/shared';
import { intakeFormStyles } from './intake-form.styles';
import { CoreTextField } from '@/modules/_core/components/_ui/core-textfield';
import { CoreSelect } from '@/modules/_core/components/_ui/core-select';
import { CoreFormControl } from '@/modules/_core/components/_ui/core-form-control';
import { clsx, IntakeFormResult, Showable, useStyles } from '@sweatpals/react-ui-kit';
import { CorePhoneInput } from '@/modules/_core/components/_ui/core-phone-input';
import { useUnifiedAnalytics } from '@sweatpals/react-web-shared';

export interface IntakeFormProps {
	disabled?: boolean;
	disabledWaiver?: boolean;
	waiverUrl?: string;
	eventPriceId?: string;
	intakeFormValues?: IntakeFormResult;
	intakeFormErrors?: Record<string, string>;
	intakeFormSettings?: EventIntakeFormSettings;
	hiddenFieldNames?: string[];
	disabledFieldNames?: string[];
	hideWaiver?: boolean;
	baseEventId?: string;
	eventInstance?: string | Date;
	changeIntakeFormValues?(key: string, value?: IntakeFormValue): void;
	onBlur?(e: FocusEvent<HTMLInputElement>): void;
}

export interface SectionFieldProps {
	field: IntakeFormSectionField;
	disabled?: boolean;
	error?: string;
	value?: IntakeFormValue;
	onChange?(key: string, value?: IntakeFormValue): void;
	onBlur?(e: FocusEvent<HTMLInputElement>): void;
}

const SectionField: FC<SectionFieldProps> = (props) => {
	const {
		field,
		disabled,
		error,
		value,
		onChange,
		onBlur,
	} = props;
	const styles = useStyles(intakeFormStyles);

	switch (field.type) {
		case 'html':
			return (
				<CoreFormControl
					label={field.label}
					description={field.description}
					controlId={field.name}
					fullWidth
				>
					<Box
						sx={{
							fontFamily: 'Montserrat',
							fontSize: '13px',
							fontWeight: 500,
							lineHeight: '16px',
							color: '#FFFFFF',
						}}
						dangerouslySetInnerHTML={{ __html: field.htmlContent }}
					/>
				</CoreFormControl>
			);
		case 'text-input':
			return field.textInputType === 'phone-number' ? (
				<CorePhoneInput
					controlId={field.name}
					id={`${field.name}-input`}
					label={field.label}
					disabled={disabled}
					name={field.name}
					description={field.description}
					requiredMark={field.required}
					placeholder={field.placeholder}
					required={field.required}
					value={(value as string) ?? ''}
					defaultCountry="US"
					forceCallingCode
					MenuProps={{ disableAutoFocusItem: true }}
					onChange={(phone) => {
						onChange?.(field.name, phone);
					}}
					onBlur={onBlur}
					error={Boolean(error)}
					helperText={error}
				/>
			) : (
				<CoreTextField
					controlId={field.name}
					id={`${field.name}-input`}
					label={field.label}
					disabled={disabled}
					required={field.required}
					name={field.name}
					description={field.description}
					requiredMark={field.required}
					placeholder={field.placeholder}
					value={value ?? ''}
					onChange={(e) => {
						onChange?.(field.name, e.target.value);
					}}
					helperText={error}
					error={Boolean(error)}
					onBlur={onBlur}
				/>
			);
		case 'check-box':
			return (
				<FormControlLabel
					id={field.name}
					disabled={disabled}
					required={field.required}
					onChange={(e, checked) => {
						onChange?.(field.name, checked);
					}}
					control={
						<Checkbox
							required={field.required}
							checked={!!value}
							checkedIcon={<CheckRounded />}
						/>
					}
					label={field.checkboxLabel}
					sx={clsx(
						styles.checkBox,
						error ? styles.checkBoxError : undefined,
					)}
				/>
			);
		case 'checkbox-list':
			return (
				<CoreFormControl
					controlId={field.name}
					requiredMark={field.required}
					label={field.label}
					description={field.description}
					error={Boolean(error)}
				>
					<FormGroup sx={{ flexDirection: 'row', gap: '16px' }}>
						{field.options?.map(option => (
							<FormControlLabel
								key={option.id}
								sx={clsx(
									styles.checkBox,
									error ? styles.checkBoxError : undefined,
								)}
								disabled={disabled}
								control={
									<Checkbox
										value={option.id}
										checked={!!value && (value as string[]).includes(option.id)}
										onChange={(e) => {
											const currentValue = (value ?? []) as string[];
											const optionId = e.target.value;
											const newValue = currentValue.includes(optionId)
												? currentValue.filter(id => id !== optionId)
												: [...currentValue, optionId];

											onChange?.(field.name, newValue);
										}}
										checkedIcon={<CheckRounded />}
									/>
								}
								label={option.name}
							/>
						))}
					</FormGroup>
				</CoreFormControl>
			);
		case 'drop-down':
			return (
				<CoreSelect
					controlId={field.name}
					requiredMark={field.required}
					required={field.required}
					label={field.label}
					description={field.description}
					disabled={disabled}
					labelId={`label-id-${field.name}`}
					value={value ?? ''}
					onChange={(e) => {
						onChange?.(field.name, e.target.value);
					}}
					helperText={error}
					error={Boolean(error)}
				>
					{field.options?.map(option => (
						<MenuItem key={option?.id} value={option?.id}>{option?.name}</MenuItem>
					))}
				</CoreSelect>
			);
		case 'radio-buttons':
			return (
				<CoreFormControl
					controlId={field.name}
					requiredMark={field.required}
					label={field.label}
					description={field.description}
					error={Boolean(error)}
				>
					<RadioGroup
						aria-labelledby={`label-id-${field.name}`}
						value={value ?? ''}
						onChange={(e, value) => {
							onChange?.(field.name, value);
						}}
						sx={styles.radioGroup}
					>
						{field.options?.map(option => (
							<FormControlLabel
								key={option?.id}
								value={option?.id}
								sx={clsx(
									styles.radioGroupOption,
									error ? styles.radioOptionError : undefined,
								)}
								disabled={disabled}
								control={<Radio />}
								label={option?.name}
							/>
						))}
					</RadioGroup>
				</CoreFormControl>
			);
		case 'date-picker':
			return (
				<CoreFormControl
					controlId={field.name}
					description={field.description}
					requiredMark={field.required}
					label={field.label}
					disabled={disabled}
					helperText={error}
					error={Boolean(error)}
				>
					<DatePicker
						disabled={disabled}
						value={value ?? null}
						onChange={(value) => {
							onChange?.(field.name, value);
						}}
						sx={styles.datePicker}
						slotProps={{
							textField: {
								error: Boolean(error)
							}
						}}
					/>
				</CoreFormControl>
			);
		default:
			return null;
	}
};

const IntakeForm: FC<IntakeFormProps> = ({
	baseEventId,
	eventInstance,
	disabled = false,
	eventPriceId,
  disabledWaiver,
  waiverUrl,
	intakeFormErrors,
	intakeFormSettings,
	intakeFormValues,
  hiddenFieldNames = [],
  disabledFieldNames = [],
  hideWaiver = false,
	changeIntakeFormValues,
	onBlur,
}) => {
	const styles = useStyles(intakeFormStyles);
	const { logEventTyped } = useUnifiedAnalytics()

	const onCheckboxChange = (_:React.SyntheticEvent<Element, Event>, checked:boolean) => {
		if (checked) {
			// track event when the checkbox is selected
			logEventTyped('checkbox_selected', {
				buttonLocation: 'intake-form',
				pageType: 'event',
				baseEventId,
				eventInstance: eventInstance instanceof Date ? eventInstance?.toISOString() : eventInstance,
			});
		}

		changeIntakeFormValues?.('waiverAgreement', checked);
	}

	const onInitialsBlur = (e:FocusEvent<HTMLInputElement, Element>) => {
		onBlur(e)

		// track event when the checkbox is selected
		logEventTyped('registration_initials_entered', {
			pageType: 'event',
			added: !!intakeFormValues?.['participantInitials'],
			baseEventId,
			eventInstance: eventInstance instanceof Date ? eventInstance?.toISOString() : eventInstance,
		});
	}

	return (
		<Box sx={styles.root} id="intake-form">
			{intakeFormSettings?.sections?.map((section, idx) => (
				<Box sx={styles.categoryBox} key={idx}>
					<Showable show={section?.sectionName}>
						<Typography sx={styles.categoryTitle}>{section?.sectionName}</Typography>
					</Showable>

					<Box sx={styles.inputsBox}>
						{section?.fields
							?.filter(f => !hiddenFieldNames.includes(f.name)
								&& (!eventPriceId || !f.tierIds || f.tierIds.includes(eventPriceId)))
							?.map(field => (
								<SectionField
									key={field.name}
									field={field}
									disabled={disabled || disabledFieldNames.includes(field.name)}
									onChange={changeIntakeFormValues}
									onBlur={onBlur}
									value={intakeFormValues?.[field.name]}
									error={intakeFormErrors?.[field.name]}
								/>
							))
						}
					</Box>
				</Box>
			))}

			<Showable hide={hideWaiver}>
				{intakeFormSettings?.waiverSections?.map((section, idx) => (
					<Box sx={styles.categoryBox} key={idx}>
						<Showable show={section?.sectionName}>
							<Typography sx={styles.categoryTitle}>{section?.sectionName}</Typography>
						</Showable>

						<Box sx={styles.inputsBox}>
							{section?.fields?.map(field => (
								<SectionField
									key={field.name}
									field={field}
									disabled={disabledWaiver}
									onChange={changeIntakeFormValues}
									onBlur={onBlur}
									value={intakeFormValues?.[field.name]}
									error={intakeFormErrors?.[field.name]}
								/>
							))}
						</Box>
					</Box>
				))}
				<Showable show={!intakeFormSettings?.waiverSections?.length && (intakeFormSettings?.waiverHtml || waiverUrl)}>
					<Box sx={styles.categoryBox}>
						<Typography sx={styles.categoryTitle}>Waiver</Typography>

						{intakeFormSettings?.waiverHtml ? (
							<Box
								sx={{
									maxHeight: '500px',
									overflow: 'auto',
									color: '#FFFFFF',
								}}
								dangerouslySetInnerHTML={{ __html: intakeFormSettings?.waiverHtml || 'waiverHTML missing' }}
							/>
						) : waiverUrl ? (
							<Typography sx={styles.waiverUrl}>
								New members: waiver must be signed before the event. Head to
								{` `}
								<Link href={waiverUrl} target="_blank">{waiverUrl}</Link>
								{` `}
								to sign the waiver.
							</Typography>
							) : null}

						<Box sx={styles.inputsBox}>
							<FormControlLabel
								id="waiverAgreement"
								disabled={disabledWaiver}
								sx={clsx(
									styles.checkBox,
									intakeFormErrors?.['waiverAgreement'] ? styles.checkBoxError : undefined,
								)}
								onChange={onCheckboxChange}
								required
								control={
									<Checkbox
										name="waiverAgreement"
										checked={!!intakeFormValues?.['waiverAgreement']}
										checkedIcon={<CheckRounded />}
									/>
								}
								label="I have reviewed and I confirm"
							/>
							<CoreTextField
								requiredMark
								required
								label="Initials of attendee (18+), parent/guardian of minor or incapacitated individual*"
								id="participantInitials"
								name="participantInitials"
								placeholder="Initials"
								value={intakeFormValues?.['participantInitials'] ?? ''}
								onBlur={onInitialsBlur}
								disabled={disabledWaiver}
								helperText={intakeFormErrors?.['participantInitials']}
								error={Boolean(intakeFormErrors?.['participantInitials'])}
								onChange={(e) => {
									changeIntakeFormValues?.('participantInitials', e.target.value);
								}}
							/>
						</Box>
					</Box>
				</Showable>
			</Showable>
		</Box>
	);
};

export default IntakeForm;
